import React, { useState } from "react";
import { PrimaryButton, SecondaryButton } from "../../../../../globalComponents/ui/Button";
import { KpiIcon } from "../KpiIcons";
import { OPEN } from "../../../KpiLock/helpers";

/** Extension lengths an administrator can grant. */
const EXTENSION_CHOICES = [2, 3, 4, 7, 15, 30];

/** Default extension when the administrator does not change it. */
const DEFAULT_EXTENSION_DAYS = 4;

/**
 * Asking for a locked KPI to be reopened, and deciding those requests.
 *
 * An administrator answers the request on the row where it is raised rather than
 * on a separate screen, so the decision is made with the KPI in view.
 *
 * A declined request is named rather than silently reverting to "Request for
 * Access" — otherwise the requester cannot tell a refusal from a request that
 * was never made, and would simply ask again.
 * @param {object} props
 * @param {object} props.item the KPI.
 * @param {object} props.lock the KPI's summarised lock state.
 * @param {boolean} props.accessRequested whether a request is awaiting a decision.
 * @param {boolean} props.accessDeclined whether the last decision was a refusal.
 * @param {boolean} props.requestingAccess whether a request is in flight.
 * @param {(item: object, lock: object) => void} props.onRequestAccess raises a request.
 * @param {boolean} props.canDecideAccess whether the viewer may approve or reject.
 * @param {number[]} props.pendingRequestIds requests awaiting this viewer's decision.
 * @param {number[]} props.pendingRequestLocationIds locations those requests cover.
 * @param {(ids: number[], days: number, locationIds: number[]) => void} props.onApproveAccess grants an extension.
 * @param {(ids: number[], locationIds: number[]) => void} props.onRejectAccess declines.
 * @returns {JSX.Element|null} the controls, or nothing when the KPI is open.
 */
const AccessRequestActions = ({
  item,
  lock,
  accessRequested = false,
  accessDeclined = false,
  requestingAccess = false,
  onRequestAccess,
  canDecideAccess = false,
  pendingRequestIds = [],
  pendingRequestLocationIds = [],
  onApproveAccess,
  onRejectAccess,
}) => {
  const [extensionDays, setExtensionDays] = useState(DEFAULT_EXTENSION_DAYS);
  const hasPendingToDecide = canDecideAccess && pendingRequestIds.length > 0;

  if (lock.status === OPEN && !hasPendingToDecide) return null;

  if (hasPendingToDecide) {
    return (
      <div
        className="kpi-no-expand"
        onClick={(e) => e.stopPropagation()}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "0 10px",
          height: 32,
          borderRadius: 6,
          background: "#fffbeb",
          border: "1px solid #fcd34d",
        }}
      >
        <span style={{ fontSize: 11.5, fontWeight: 600, color: "#92400e", fontFamily: "var(--font-mono)" }}>
          {pendingRequestIds.length} access request{pendingRequestIds.length > 1 ? "s" : ""}
        </span>
        <label style={{ fontSize: 11.5, color: "#92400e" }}>
          Extend by
          <select
            value={extensionDays}
            onChange={(e) => setExtensionDays(Number(e.target.value))}
            style={{ marginLeft: 6, fontSize: 11.5, padding: "2px 4px", borderRadius: 4, border: "1px solid #fcd34d" }}
          >
            {EXTENSION_CHOICES.map((days) => (
              <option key={days} value={days}>{days} days</option>
            ))}
          </select>
        </label>
        <PrimaryButton
          style={{ height: 26, padding: "0 10px", fontSize: 11.5, minWidth: "auto" }}
          onClick={() => onApproveAccess?.(pendingRequestIds, extensionDays, pendingRequestLocationIds)}
        >
          Approve
        </PrimaryButton>
        <SecondaryButton
          style={{ height: 26, padding: "0 10px", fontSize: 11.5, minWidth: "auto" }}
          onClick={() => onRejectAccess?.(pendingRequestIds, pendingRequestLocationIds)}
        >
          Reject
        </SecondaryButton>
      </div>
    );
  }

  return (
    <SecondaryButton
      style={{ height: 32, padding: "0 14px", fontSize: 12, minWidth: "auto" }}
      disabled={accessRequested || requestingAccess}
      title={
        accessRequested
          ? "An administrator has yet to review this request"
          : accessDeclined
            ? "An administrator declined the last request. You can ask again."
            : undefined
      }
      onClick={(e) => { e.stopPropagation(); onRequestAccess?.(item, lock); }}
    >
      <KpiIcon name="unlock" size={11} />
      {accessRequested
        ? "Access requested"
        : requestingAccess
          ? "Requesting…"
          : accessDeclined
            ? "Request declined — ask again"
            : "Request for Access"}
    </SecondaryButton>
  );
};

export default AccessRequestActions;
